import { ensureInboundAlerts, INBOUND_ALERT_TYPES, listInboundAlerts } from '@api/inboundAlerts';
import { getPayables } from '@api/mother-order';
import { listShipments, type Shipment } from '@api/shipments';
import PageHeader from '@components/common/PageHeader';
import useToaster from '@hooks/toaster/useToaster';
import { useCbmEnabled } from '@hooks/useCbmEnabled';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { Box, Button, Group, Select, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FC, useMemo, useState } from 'react';

import {
    formatDateDisplay,
    StatusBadge,
    textColors,
} from '../procurementOrders/shared';

import { PaymentChip, summarizePayables } from '../payments';
import { shipmentNeeds } from '../orders/inbound';
import { NeedChips } from '../orders/NeedChip';
import ShipmentDetailPanel from './ShipmentDetailPanel';
import { formatCount } from '@utils/currency';

const num = (n: number): string => formatCount(n || 0);

const STATUS_FILTERS = [
    { value: 'ALL', label: 'All statuses' },
    { value: 'READY_TO_SHIP', label: 'Ready to Ship' },
    { value: 'SHIPPED', label: 'Shipped' },
    { value: 'AT_DESTINATION', label: 'At Destination Port' },
    { value: 'PARTIALLY_RECEIVED', label: 'Partially Received' },
    { value: 'RECEIVED', label: 'Received' },
    { value: 'CANCELLED', label: 'Cancelled' },
];

const ShipmentsContainer: FC = () => {
    const queryClient = useQueryClient();
    const cbmEnabled = useCbmEnabled();
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const { showToastSuccess, showToastError } = useToaster();
    // The four inbound alerts (forwarder, dispatch, arrival, payment): on or not, per brand.
    const { data: inboundAlerts } = useQuery(['inbound-alerts'], listInboundAlerts, { staleTime: 60_000 });
    const alertsMissing = inboundAlerts ? INBOUND_ALERT_TYPES.length - inboundAlerts.size : 0;
    const [turningOn, setTurningOn] = useState(false);
    const turnOnAlerts = async () => {
        setTurningOn(true);
        try {
            const created = await ensureInboundAlerts();
            showToastSuccess({
                title: created > 0 ? `${created} alert${created === 1 ? '' : 's'} turned on` : 'Shipment alerts are already on',
                subtitle: 'Checked every morning; each fires a card on Home listing the shipments or payments concerned.',
            });
            void queryClient.invalidateQueries(['inbound-alerts']);
        } catch (e: any) {
            showToastError({ title: 'Could not turn on the alerts', subtitle: e?.error?.message || e?.message || 'Please retry.' });
        } finally {
            setTurningOn(false);
        }
    };

    const { data: payablesView } = useQuery(['payables', true], () => getPayables(true));
    const payablesIndex = useMemo(() => summarizePayables(payablesView), [payablesView]);
    const { data: shipments, isLoading } = useQuery(['shipments'], () =>
        listShipments(),
    );

    const rows = useMemo(() => {
        const all = (shipments ?? []) as Shipment[];
        if (statusFilter === 'ALL') return all;
        return all.filter((s) => s.status === statusFilter);
    }, [shipments, statusFilter]);

    // Derive the selected shipment from the (re-fetchable) list by id so it
    // stays fresh after a warehouse change recomputes the expected timeline.
    const selected = useMemo(
        () => (shipments ?? []).find((s) => s.poNumber === selectedId) ?? null,
        [shipments, selectedId],
    );

    const columns: DataSheetColumn<Shipment>[] = useMemo(
        () => [
            {
                key: 'poNumber',
                header: 'Shipment',
                width: 160,
                sticky: true,
                render: (v: string) => (
                    <span style={{ fontSize: 13, fontWeight: 600, color: textColors.primary }}>{v}</span>
                ),
            },
            {
                key: 'motherOrderNumber',
                header: 'Parent Order',
                width: 160,
                render: (v: string | null) => (
                    <span style={{ fontSize: 13, color: v ? textColors.primary : textColors.muted }}>{v || '—'}</span>
                ),
            },
            {
                key: 'vendorName',
                header: 'Vendor',
                width: 200,
                render: (v: string | null) => (
                    <span style={{ fontSize: 13, color: v ? textColors.primary : textColors.muted }}>{v || 'No vendor'}</span>
                ),
            },
            {
                key: 'status',
                header: 'Status',
                width: 160,
                render: (v: string) => <StatusBadge status={v} />,
            },
            {
                key: 'needs',
                header: 'Needs',
                width: 190,
                render: (_: unknown, r: Shipment) => <NeedChips needs={shipmentNeeds(r)} />,
            },
            {
                key: 'payments',
                header: 'Payments',
                width: 170,
                render: (_: unknown, r: Shipment) => (
                    <PaymentChip summary={payablesIndex.byPoId.get(r.poNumber)} />
                ),
            },
            {
                key: 'departureDate',
                header: 'Dispatched',
                width: 130,
                render: (v: string | null) => (
                    <span style={{ fontSize: 13, color: textColors.primary }}>{v ? formatDateDisplay(v) : '—'}</span>
                ),
            },
            {
                key: 'deliveryDate',
                header: 'Delivered',
                width: 130,
                render: (v: string | null) => (
                    <span style={{ fontSize: 13, color: textColors.primary }}>{v ? formatDateDisplay(v) : '—'}</span>
                ),
            },
            {
                key: 'totalUnits',
                header: 'Shipment Qty',
                width: 120,
                align: 'right' as const,
                render: (v?: number) => (
                    <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{num(v ?? 0)}</span>
                ),
            },
            ...(cbmEnabled
                ? [
                      {
                          key: 'totalCbm',
                          header: 'CBM',
                          width: 100,
                          align: 'right' as const,
                          render: (v: number | null) => (
                              <span style={{ fontSize: 13, color: v == null ? textColors.muted : textColors.primary, fontVariantNumeric: 'tabular-nums' }}>
                                  {v == null ? '—' : formatCount(v, { decimals: 2 })}
                              </span>
                          ),
                      },
                  ]
                : []),
            {
                key: 'receivedUnits',
                header: 'Received',
                width: 120,
                align: 'right' as const,
                render: (v: number) => (
                    <span style={{ fontSize: 13, color: (v ?? 0) > 0 ? textColors.primary : textColors.muted, fontVariantNumeric: 'tabular-nums' }}>
                        {num(v ?? 0)}
                    </span>
                ),
            },
        ],
        [payablesIndex, cbmEnabled],
    );

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <Group justify="space-between" align="flex-start">
                <PageHeader
                    icon="🚚"
                    title="Shipments"
                    subtitle="Track every shipment across your orders"
                />
                <Group gap={8}>
                    {alertsMissing > 0 && (
                        <Button
                            size="xs"
                            variant="light"
                            onClick={() => void turnOnAlerts()}
                            loading={turningOn}
                            title="Forwarder not booked 10 days before ready, dispatch date passed, arrival date passed, payment due in 2 days. Checked every morning; each fires a card on Home."
                        >
                            Turn on shipment alerts{alertsMissing < INBOUND_ALERT_TYPES.length ? ` (${alertsMissing} off)` : ''}
                        </Button>
                    )}
                    <Select
                        data={STATUS_FILTERS}
                        value={statusFilter}
                        onChange={(v) => setStatusFilter(v || 'ALL')}
                        w={200}
                        aria-label="Filter by status"
                    />
                </Group>
            </Group>

            <Box>
                <DataSheet
                    data={rows}
                    columns={columns}
                    isLoading={isLoading}
                    getRowKey={(row) => row.poNumber}
                    title="Shipments"
                    subtitle={`${rows.length} shipments`}
                    enableSearch
                    searchPlaceholder="Search by shipment or order..."
                    onRowClick={(row) => setSelectedId(row.poNumber)}
                    emptyState={
                        <Text size="13px" c="dimmed" ta="center" p={32}>
                            No shipments yet. Add shipments against an order to see them here.
                        </Text>
                    }
                />
            </Box>

            <ShipmentDetailPanel
                shipment={selected}
                onClose={() => setSelectedId(null)}
                onChanged={() => queryClient.invalidateQueries(['shipments'])}
            />
        </div>
    );
};

export default ShipmentsContainer;
